import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BadgeCheck, Bell, BellOff, Mail, MessageCircle, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/PhoneInput';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { disablePush, enablePush, pushState, type PushState } from '@/lib/push';

// Preferências dos avisos de "ação necessária na sua integração".
//
// O WhatsApp só liga com telefone VERIFICADO (código recebido pelo próprio
// bZapper) e opt-in explícito. Não é burocracia: sem isso o aviso seria disparo
// não solicitado para um número que ninguém provou — o anti-padrão que o produto
// ensina o cliente a evitar, feito com o número oficial.
interface Prefs {
  email: string;
  phone: string;
  phone_verified: boolean;
  notify_whatsapp: boolean;
  whatsapp_available: boolean;
  push_enabled: boolean;
}

export function NotificationPrefs() {
  const t = useT();
  const qc = useQueryClient();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [push, setPush] = useState<PushState>('off');
  const [pushBusy, setPushBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ['notify-prefs'],
    queryFn: async (): Promise<Prefs | null> => {
      const r = await apiFetch('GET', '/me/notifications');
      return r.ok ? (r.data as Prefs) : null;
    },
  });

  useEffect(() => {
    void pushState().then(setPush);
  }, []);

  const setWhats = useMutation({
    mutationFn: async (on: boolean) => {
      const r = await apiFetch('PATCH', '/me/notifications', { notify_whatsapp: on });
      if (!r.ok) throw new Error((r.data as { code?: string })?.code ?? 'failed');
    },
    onSuccess: () => {
      toast.success(t('settings.notifications.saved'));
      void qc.invalidateQueries({ queryKey: ['notify-prefs'] });
    },
    onError: (e: Error) => {
      toast.error(
        e.message === 'phone_not_verified'
          ? t('settings.notifications.whatsappNeedsVerify')
          : t('settings.saveFailed'),
      );
    },
  });

  const sendCode = useMutation({
    mutationFn: async () => {
      const r = await apiFetch('POST', '/me/phone/verify/start', { phone: phone || data?.phone });
      if (!r.ok) throw new Error((r.data as { message?: string })?.message ?? 'failed');
    },
    onSuccess: () => {
      setCodeSent(true);
      toast.success(t('settings.notifications.codeSent'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const confirmCode = useMutation({
    mutationFn: async () => {
      const r = await apiFetch('POST', '/me/phone/verify/confirm', { code });
      if (!r.ok) throw new Error((r.data as { message?: string })?.message ?? 'failed');
    },
    onSuccess: () => {
      setCodeSent(false);
      setCode('');
      toast.success(t('settings.notifications.verified'));
      void qc.invalidateQueries({ queryKey: ['notify-prefs'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const togglePush = async () => {
    setPushBusy(true);
    try {
      setPush(push === 'on' ? await disablePush() : await enablePush());
    } finally {
      setPushBusy(false);
    }
  };

  if (!data) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.notifications.title')}</CardTitle>
        <CardDescription>{t('settings.notifications.desc')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6 text-sm">
        {/* E-mail — sempre ligado para admins; não há o que configurar. */}
        <div className="flex items-start gap-3">
          <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">{t('settings.notifications.email')}</p>
            <p className="text-muted-foreground">{data.email}</p>
            <p className="text-xs text-muted-foreground">{t('settings.notifications.emailAlways')}</p>
          </div>
        </div>

        {/* Push do navegador */}
        <div className="flex items-start gap-3">
          {push === 'on' ? (
            <Bell className="mt-0.5 size-4 shrink-0 text-primary" />
          ) : (
            <BellOff className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          )}
          <div className="min-w-0 flex-1">
            <p className="font-medium">{t('settings.notifications.push')}</p>
            {push === 'unsupported' && (
              <p className="text-muted-foreground">{t('settings.notifications.pushUnsupported')}</p>
            )}
            {push === 'unavailable' && (
              <p className="text-muted-foreground">{t('settings.notifications.pushUnavailable')}</p>
            )}
            {push === 'denied' && (
              <p className="text-muted-foreground">{t('settings.notifications.pushDenied')}</p>
            )}
            {(push === 'on' || push === 'off') && (
              <Button variant="outline" size="sm" className="mt-1.5" disabled={pushBusy} onClick={togglePush}>
                {push === 'on' ? t('settings.notifications.pushOff') : t('settings.notifications.pushOn')}
              </Button>
            )}
          </div>
        </div>

        {/* WhatsApp — exige verificação + opt-in */}
        <div className="flex items-start gap-3">
          <MessageCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">{t('settings.notifications.whatsapp')}</p>

            <div className="mt-1 flex items-center gap-2">
              <span className="text-muted-foreground">{data.phone || '—'}</span>
              {data.phone_verified ? (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                  <BadgeCheck className="size-3.5" /> {t('settings.notifications.phoneVerified')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600">
                  <ShieldAlert className="size-3.5" /> {t('settings.notifications.phoneUnverified')}
                </span>
              )}
            </div>

            {!data.phone_verified && data.whatsapp_available && (
              <div className="mt-3 flex flex-col gap-3 rounded-md border border-border p-3">
                {!codeSent ? (
                  <>
                    <div className="flex flex-col gap-2">
                      <Label>{t('settings.notifications.phone')}</Label>
                      <PhoneInput value={data.phone} onChange={setPhone} />
                    </div>
                    <div>
                      <Button size="sm" disabled={sendCode.isPending} onClick={() => sendCode.mutate()}>
                        {t('settings.notifications.sendCode')}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="vcode">{t('settings.notifications.code')}</Label>
                    <div className="flex gap-2">
                      <Input
                        id="vcode"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="000000"
                        className="max-w-[10rem]"
                      />
                      <Button size="sm" disabled={code.length < 6 || confirmCode.isPending} onClick={() => confirmCode.mutate()}>
                        {t('settings.notifications.confirm')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {data.phone_verified && (
              <label className="mt-2 flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  className="size-4 accent-primary"
                  checked={data.notify_whatsapp}
                  disabled={setWhats.isPending}
                  onChange={(e) => setWhats.mutate(e.target.checked)}
                />
                <span>{t('settings.notifications.whatsappOn')}</span>
              </label>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
